import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, CalendarCheck, AlertTriangle, CheckCheck, Clock, MessageSquare, Crown, Mail } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

const ICONS = { booking: CalendarCheck, dispute: AlertTriangle, claim: AlertTriangle, reminder: Clock, review_reply: MessageSquare, plan: Crown, email: Mail };

function timeAgo(iso, prefix) {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${prefix} ${diff}s`;
  if (diff < 3600) return `${prefix} ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${prefix} ${Math.floor(diff / 3600)}h`;
  return `${prefix} ${Math.floor(diff / 86400)}j`;
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const onNotifClick = (n) => {
    if (n.type === "email" && n.ref) {
      setOpen(false);
      navigate(`/admin?tab=emails&email=${encodeURIComponent(n.ref)}`);
      return;
    }
    if (n.type === "claim") {
      setOpen(false);
      const role = user?.role;
      if (role === "superadmin" || role === "sous-admin") navigate("/admin?tab=claims");
      else if (role === "loueur") navigate("/loueur");
      else navigate("/my/bookings");
    }
  };

  const load = useCallback(() => {
    api.get("/notifications").then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setUnread(0); setItems((x) => x.map((n) => ({ ...n, read: true }))); } catch (e) { console.error("mark all notifications read failed", e); }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) markAll();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 rounded-full hover:bg-sand transition-colors">
          <Bell className="w-5 h-5 text-ink" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emeraude text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl overflow-hidden" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <p className="font-heading font-bold text-ink text-sm">{t("notif.title")}</p>
          {items.length > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs font-semibold text-canard hover:underline flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> {t("notif.markAll")}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted2 font-body">{t("notif.empty")}</div>
        ) : (
          <ScrollArea className="max-h-80">
            <ul>
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                const clickable = (n.type === "email" && n.ref) || n.type === "claim";
                return (
                  <li key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => onNotifClick(n)} className={`flex gap-3 px-4 py-3 border-b border-border/60 ${n.read ? "" : "bg-emeraude/5"} ${clickable ? "cursor-pointer hover:bg-sand" : ""}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === "dispute" || n.type === "claim" ? "bg-yellow-100 text-yellow-600" : "bg-canard/10 text-canard"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{n.title}</p>
                      <p className="text-xs text-muted2 font-body line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-muted2 mt-0.5">{timeAgo(n.created_at, t("notif.ago"))}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
